import type { Release, ReleaseColumnConfig, ReleasesResponse } from '../types';

export const COMBINED_RELEASE_TAB = 'all_sources';
export const COMBINED_RELEASE_SOURCE_NAMES = ['direct_download', 'prowlarr'] as const;

interface ReleaseTab {
  name: string;
  displayName: string;
  enabled: boolean;
}

const COMBINED_COLUMN_CONFIG: ReleaseColumnConfig = {
  columns: [
    {
      key: 'extra.source_label',
      label: 'Source',
      render_type: 'badge',
      align: 'center',
      width: '105px',
      hide_mobile: false,
      fallback: '',
      uppercase: false,
    },
    {
      key: 'extra.indexer_flags',
      label: 'Flags',
      render_type: 'tags',
      align: 'center',
      width: '90px',
      hide_mobile: false,
      fallback: '',
      uppercase: false,
    },
    {
      key: 'language',
      label: 'Language',
      render_type: 'badge',
      align: 'center',
      width: '60px',
      hide_mobile: true,
      color_hint: { type: 'map', value: 'language' },
      fallback: '',
      uppercase: true,
    },
    {
      key: 'format',
      label: 'Format',
      render_type: 'badge',
      align: 'center',
      width: '80px',
      hide_mobile: false,
      color_hint: { type: 'map', value: 'format' },
      fallback: '',
      uppercase: true,
    },
    {
      key: 'size',
      label: 'Size',
      render_type: 'size',
      align: 'center',
      width: '80px',
      hide_mobile: false,
      fallback: '',
      uppercase: false,
      sortable: true,
      sort_key: 'size_bytes',
    },
  ],
  grid_template: 'minmax(0,2fr) 105px 90px 60px 80px 80px',
  supported_filters: ['format', 'language', 'indexer'],
  extra_sort_options: [{ label: 'Peers', sort_key: 'seeders' }],
};

export function joinDirectAndProwlarrTabs(tabs: ReleaseTab[]): ReleaseTab[] {
  const names = new Set(tabs.map((tab) => tab.name));
  if (!COMBINED_RELEASE_SOURCE_NAMES.every((sourceName) => names.has(sourceName))) {
    return tabs;
  }

  const firstJoinedIndex = tabs.findIndex((tab) =>
    COMBINED_RELEASE_SOURCE_NAMES.includes(
      tab.name as (typeof COMBINED_RELEASE_SOURCE_NAMES)[number],
    ),
  );
  const remaining = tabs.filter(
    (tab) =>
      !COMBINED_RELEASE_SOURCE_NAMES.includes(
        tab.name as (typeof COMBINED_RELEASE_SOURCE_NAMES)[number],
      ),
  );
  remaining.splice(firstJoinedIndex, 0, {
    name: COMBINED_RELEASE_TAB,
    displayName: 'Direct + Prowlarr',
    enabled: true,
  });
  return remaining;
}

export function getReleaseSourcesForTab(tabName: string): string[] {
  return tabName === COMBINED_RELEASE_TAB ? [...COMBINED_RELEASE_SOURCE_NAMES] : [tabName];
}

export function resolveInitialReleaseTab(preferredSource: string, tabs: ReleaseTab[]): string {
  if (
    COMBINED_RELEASE_SOURCE_NAMES.includes(
      preferredSource as (typeof COMBINED_RELEASE_SOURCE_NAMES)[number],
    ) &&
    tabs.some((tab) => tab.name === COMBINED_RELEASE_TAB)
  ) {
    return COMBINED_RELEASE_TAB;
  }
  return preferredSource;
}

function sourceLabel(release: Release): string {
  if (release.source === 'direct_download') return 'Direct';
  if (release.source === 'prowlarr') return release.indexer || 'Prowlarr';
  return release.source;
}

function normalizeRelease(release: Release): Release {
  const extra = release.extra ?? {};
  const extraLanguage = extra.language;
  return {
    ...release,
    // The combined tab's indexer selector is a Prowlarr filter. Direct
    // downloads currently serialize their source label into `indexer`, which
    // makes the modal mistake them for an unselected Prowlarr indexer and hide
    // them whenever Prowlarr has default indexers configured. The source label
    // remains available through extra.source_label below.
    indexer: release.source === 'direct_download' ? undefined : release.indexer,
    language:
      release.language ?? (typeof extraLanguage === 'string' ? extraLanguage : undefined),
    extra: {
      ...extra,
      source_label: sourceLabel(release),
    },
  };
}

export function mergeReleaseSourceResponses(responses: ReleasesResponse[]): ReleasesResponse {
  if (responses.length === 0) {
    throw new Error('No release source returned a response');
  }

  const prowlarrConfig = responses.find((response) =>
    response.sources_searched.includes('prowlarr'),
  )?.column_config;
  const searchInfo = Object.assign({}, ...responses.map((response) => response.search_info ?? {}));
  const combinedSearchType = Object.values(searchInfo).some(
    (info) =>
      typeof info === 'object' &&
      info !== null &&
      'search_type' in info &&
      info.search_type === 'expanded',
  )
    ? 'expanded'
    : 'categories';

  return {
    ...responses[0],
    releases: responses.flatMap((response) => response.releases.map(normalizeRelease)),
    sources_searched: Array.from(
      new Set(responses.flatMap((response) => response.sources_searched)),
    ),
    errors: responses.flatMap((response) => response.errors ?? []),
    column_config: {
      ...COMBINED_COLUMN_CONFIG,
      available_indexers: prowlarrConfig?.available_indexers,
      default_indexers: prowlarrConfig?.default_indexers,
    },
    search_info: {
      ...searchInfo,
      [COMBINED_RELEASE_TAB]: { search_type: combinedSearchType },
    },
  };
}
